import { useEffect, useMemo, useState } from 'react'
import { PendingConsultationBadge } from '@/components/chat/PendingConsultationBadge'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { conversationSocket } from '@/services/conversationSocket'
import {
  Search,
  RefreshCw,
  Filter,
  MessageSquare,
  X,
  Loader2,
  Users,
  Send,
  MessageCircle,
  Globe,
  ExternalLink,
  CheckCheck,
  Inbox,
  CheckCircle2,
  Tag as TagIcon,
  ShoppingBag,
  Bot,
  AlertCircle,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { MultiSelect } from '@/components/ui/multi-select'
import Modal from '@/components/ui/modal'
import RowActionsMenu from '@/components/conversations/RowActionsMenu'
import ConversationListSkeleton from '@/components/conversations/ConversationListSkeleton'
import { ChannelFilterBar } from '@/components/conversations/ChannelFilterBar'
import { conversationsApi, type ConversationFilters, type ConversationViewCounts, type InitiateConversationRequest } from '@/services/conversationsApi'
import { teamsApi } from '@/services/teamsApi'
import { listChannelInstances, type ChannelInstanceResponse } from '@/services/channelsApi'
import { tagsApi, Tag } from '@/services/tagsApi'
import { useConversationsList, useConversationsActions, useConversationsStore } from '@/store/conversationsStore'
import { useAuthStore } from '@/store/authStore'
import { defineAbilityFor } from '@/lib/ability'

import hotToast from 'react-hot-toast'

// Toast helper backed by react-hot-toast
const toast = {
  success: (title: string, options?: { description?: string }) => {
    hotToast.success(options?.description ? `${title}: ${options.description}` : title)
  },
  error: (title: string, options?: { description?: string }) => {
    hotToast.error(options?.description ? `${title}: ${options.description}` : title)
  },
}

const WON_CODES = new Set(['SALE_CONFIRMED', 'PAYMENT_RECEIVED', 'ORDER_DELIVERED'])

const CLOSURE_REASON_LABELS: Record<string, string> = {
  won: 'Solo Ventas Ganadas (Won)',
  lost: 'Solo No Concretadas (Lost)',
  SALE_CONFIRMED: 'Pedido Confirmado',
  PAYMENT_RECEIVED: 'Pago Verificado',
  ORDER_DELIVERED: 'Pedido Entregado',
  ABANDONED_CART: 'Carrito Abandonado',
  OUT_OF_COVERAGE: 'Fuera de Cobertura',
  OUT_OF_STOCK: 'Sin Stock / Agotado',
  PRICE_OBJECTION: 'Objeción de Precio',
  CANCELLED_BY_CLIENT: 'Cancelado por el Cliente',
  CUSTOMER_SUPPORT: 'Consulta de Soporte',
  SPAM_OR_WRONG_NUMBER: 'Spam o Número Erróneo',
}

const CLOSURE_SHORT_LABELS: Record<string, string> = {
  SALE_CONFIRMED: 'VENTA',
  PAYMENT_RECEIVED: 'PAGADO',
  ORDER_DELIVERED: 'ENTREGADO',
  ABANDONED_CART: 'ABANDONADO',
  OUT_OF_COVERAGE: 'SIN ZONA',
  OUT_OF_STOCK: 'SIN STOCK',
  PRICE_OBJECTION: 'PRECIO',
  CANCELLED_BY_CLIENT: 'CANCELADO',
  CUSTOMER_SUPPORT: 'SOPORTE',
  SPAM_OR_WRONG_NUMBER: 'SPAM',
}

function isClosureWon(reason?: string | null): boolean {
  return reason ? WON_CODES.has(reason) : false
}

function getClosureShortLabel(reason?: string | null): string {
  if (!reason) return 'CERRADA'
  return CLOSURE_SHORT_LABELS[reason] || 'CERRADA'
}

function getReasonFilterLabel(reason?: string): string {
  if (!reason) return ''
  return CLOSURE_REASON_LABELS[reason] || reason
}

type TabType = 'mine' | 'team' | 'all'

export default function ConversationsList() {
  const navigate = useNavigate()
  const { id: openConversationId } = useParams<{ id?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { conversations, totalPages, currentPage, isLoading, error, filters } = useConversationsList()
  const { setConversations, setFilters, setLoading, setError, updateConversation } = useConversationsActions()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabType>('mine')
  const [claimingId, setClaimingId] = useState<string | null>(null)
  const [transferringId, setTransferringId] = useState<string | null>(null)
  const [showTeamSelector, setShowTeamSelector] = useState<string | null>(null)
  const [showNewChatModal, setShowNewChatModal] = useState(false)
  const [newChatPhone, setNewChatPhone] = useState('')
  const [newChatMessage, setNewChatMessage] = useState('Hola, quería escribirte por WhatsApp.')
  const [selectedChannelInstanceId, setSelectedChannelInstanceId] = useState<string | null>(null)

  // Check user abilities
  const userRole = user?.role || 'agent'
  const ability = defineAbilityFor(userRole)
  const canViewAllConversations = ability.can('view', 'AllConversations')

  const [localFilters, setLocalFilters] = useState<ConversationFilters>({
    ...filters,
    status: filters.status || 'open', // Asegurar que el filtro por defecto sea 'open'
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  // La campana del dashboard abre la bandeja con ?hasUnread=true: la URL es
  // la fuente del filtro de no leídas, así que no toca el store de filtros.
  const hasUnreadFilter = searchParams.get('hasUnread') === 'true' || Boolean(filters.hasUnread)
  const queryFilters: ConversationFilters = hasUnreadFilter
    ? { ...filters, hasUnread: true }
    : filters

  // Query para obtener conversaciones
  const { data, isLoading: queryLoading, error: queryError, refetch } = useQuery({
    queryKey: ['conversations', queryFilters],
    queryFn: () => conversationsApi.getConversations(queryFilters),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  // Query para obtener equipos
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getTeams(),
    enabled: canViewAllConversations, // Solo cargar si puede ver todas las conversaciones
    staleTime: 60000, // 1 minuto
  })

  // Query para obtener tags
  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.getAll(),
    staleTime: 300000, // 5 minutos
  })

  const { data: channelInstances = [] } = useQuery({
    queryKey: ['channel-instances'],
    queryFn: () => listChannelInstances(),
    staleTime: 60000,
  })

  // Contadores reales de pestañas (Asignados / Equipo / Todos / Abiertas / Cerradas) desde el backend
  const { data: viewCounts } = useQuery({
    queryKey: ['conversations', 'counts', filters.channelType ?? 'all', filters.mode ?? 'all', filters.status ?? 'open'],
    queryFn: () =>
      conversationsApi.getViewCounts({
        channelType: filters.channelType,
        mode: filters.mode,
        status: filters.status || 'open',
      }),
    staleTime: 15000,
    refetchOnWindowFocus: true,
  }) as { data?: ConversationViewCounts }

  const handleStatusChange = (newStatus: 'open' | 'closed') => {
    setLocalFilters((prev) => ({
      ...prev,
      status: newStatus,
      closeReason: newStatus === 'closed' ? prev.closeReason : undefined,
    }))
    setFilters({
      ...filters,
      status: newStatus,
      closeReason: newStatus === 'closed' ? filters.closeReason : undefined,
      page: 1,
    })
  }

  // Convertir tags al formato para MultiSelect
  const tagOptions = (tagsData || []).map((tag: Tag) => ({
    id: tag.id,
    label: tag.name,
  }))

  // State para tags seleccionados
  const [selectedTags, setSelectedTags] = useState<string[]>(
    filters.tags || []
  )

  // Sincronizar con el store
  useEffect(() => {
    setLoading(queryLoading)
  }, [queryLoading, setLoading])

  useEffect(() => {
    if (queryError) {
      setError(queryError instanceof Error ? queryError.message : 'Error al cargar conversaciones')
    } else {
      setError(null)
    }
  }, [queryError, setError])

  useEffect(() => {
    if (data) {
      // Preservar el unreadCount local cuando se sincroniza desde el backend
      // Si la conversación está abierta o se marcó como leída localmente (0), mantener 0
      const currentConversations = useConversationsStore.getState().conversations
      const updatedConversations = data.conversations.map((conv) => {
        if (openConversationId && conv.id === openConversationId) {
          return { ...conv, unreadCount: 0 }
        }
        const localConv = currentConversations.find(c => c.id === conv.id)
        if (localConv && localConv.unreadCount === 0) {
          return { ...conv, unreadCount: 0 }
        }
        // Si tenemos un contador local mayor, preservarlo
        if (localConv && localConv.unreadCount > conv.unreadCount) {
          return { ...conv, unreadCount: localConv.unreadCount }
        }
        return conv
      })
      setConversations(updatedConversations, data.pagination.totalPages, data.pagination.page)
    }
  }, [data, setConversations, openConversationId])

  useEffect(() => {
    if (selectedChannelInstanceId) return
    const firstWa = (channelInstances as ChannelInstanceResponse[]).find((instance) =>
      instance.channelType === 'WHATSAPP' || instance.channelType === 'WHATSAPP_CLOUD'
    )
    if (firstWa) {
      setSelectedChannelInstanceId(firstWa.id)
    }
  }, [channelInstances, selectedChannelInstanceId])

  // WebSocket unificado para recibir nuevos mensajes en tiempo real
  useEffect(() => {
    const unsubscribe = conversationSocket.subscribe({
      onConversationUpdated: (event) => {
        if (event.conversationId && event.mode) {
          updateConversation(event.conversationId, { mode: event.mode })
        }
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      },
      onChatEvent: (event) => {
        if (event.type === 'messages.cleared') {
          updateConversation(event.conversationId, {
            lastMessage: undefined,
            lastMessageAt: undefined,
            unreadCount: 0,
          })
          queryClient.invalidateQueries({ queryKey: ['conversations'] })
          queryClient.invalidateQueries({ queryKey: ['conversations', 'counts'] })
          return
        }

        // Solo procesar eventos de mensajes nuevos
        if (event.type === 'message' || event.type === 'message.media') {
          const currentConversations = useConversationsStore.getState().conversations
          const conversation = currentConversations.find((c) => c.id === event.conversationId)
          const isIncoming = event.data?.fromMe === false

          if (conversation) {
            if (isIncoming) {
              const isCurrentlyOpen = Boolean(openConversationId && event.conversationId === openConversationId)
              updateConversation(event.conversationId, {
                unreadCount: isCurrentlyOpen ? 0 : (conversation.unreadCount || 0) + 1,
                lastMessage: event.data?.text || event.data?.caption || '📎 Archivo',
                lastMessageAt: new Date(event.timestamp),
              })
            } else {
              updateConversation(event.conversationId, {
                lastMessage: event.data?.text || event.data?.caption || '📎 Archivo',
                lastMessageAt: new Date(event.timestamp),
              })
            }
          } else {
            queryClient.invalidateQueries({ queryKey: ['conversations'] })
          }
        }
      },
      onReconnect: () => {
        // Revalidar conversaciones al reconectar el socket para evitar desincronización
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      },
    })

    return () => {
      unsubscribe()
    }
  }, [queryClient, updateConversation, openConversationId])

  // Debounce para búsqueda en tiempo real
  useEffect(() => {
    if (searchTerm === filters.search) return

    setIsSearching(true)
    const timeoutId = setTimeout(() => {
      setFilters({
        ...filters,
        search: searchTerm || undefined,
        page: 1,
      })
      setIsSearching(false)
    }, 500) // 500ms de delay

    return () => {
      clearTimeout(timeoutId)
      setIsSearching(false)
    }
  }, [searchTerm])

  // Aplicar filtros
  const handleApplyFilters = () => {
    setFilters({
      ...localFilters,
      search: searchTerm || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      page: 1,
    })
    setShowFilters(false)
  }

  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchTerm('')
    setFilters({
      ...filters,
      search: undefined,
      page: 1,
    })
  }

  // Limpiar filtros (restaurar a valores por defecto: solo abiertas)
  const handleClearFilters = () => {
    const clearedFilters = {
      status: 'open' as const,
      mode: 'all' as const,
      channelType: 'all' as const,
      tags: [] as string[],
      closeReason: undefined,
      hasUnread: undefined,
      page: 1,
      limit: 20,
    }
    setLocalFilters(clearedFilters)
    setSelectedTags([])
    setSearchTerm('')
    setFilters(clearedFilters)
    if (searchParams.has('hasUnread')) setSearchParams({}, { replace: true })
  }

  // Cambiar página
  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page })
  }

  // Mutation para claim
  const claimMutation = useMutation({
    mutationFn: (conversationId: string) => conversationsApi.claimConversation(conversationId),
    onMutate: async (conversationId) => {
      setClaimingId(conversationId)

      // Optimistic update: mover de team queue a mine
      const currentConversations = useConversationsStore.getState().conversations
      const claimedConv = currentConversations.find(c => c.id === conversationId)

      if (claimedConv && user) {
        updateConversation(conversationId, {
          assignedUserId: user.id,
          status: 'open' as any,
        })
      }
    },
    onSuccess: (_data, conversationId) => {
      toast.success('Chat tomado exitosamente', {
        description: 'Ahora puedes responder a este cliente',
      })

      // Cambiar a pestaña Asignados para mantener visible el chat tomado
      setActiveTab('mine')

      // Navegar al chat indicando que fue tomado para forzar scroll al mensaje y foco en el input
      navigate(`/app/conversations/${conversationId}?claimed=true`)

      // Invalidar queries para refrescar
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
    },
    onError: (error: any, conversationId) => {
      setClaimingId(null)

      // Revertir optimistic update
      const currentConversations = useConversationsStore.getState().conversations
      const conv = currentConversations.find(c => c.id === conversationId)

      if (conv) {
        updateConversation(conversationId, {
          assignedUserId: null,
          status: 'QUEUED' as any,
        })
      }

      // Mostrar error específico
      if (error.response?.status === 409) {
        toast.error('Chat ya asignado', {
          description: 'Otro agente tomó este chat antes que tú',
        })
      } else if (error.response?.status === 403) {
        toast.error('Sin permisos', {
          description: 'No tienes permiso para tomar este chat',
        })
      } else {
        toast.error('Error al tomar el chat', {
          description: error.response?.data?.message || error.response?.data?.error || 'Intenta nuevamente',
        })
      }

      // Refrescar lista
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onSettled: () => {
      setClaimingId(null)
    },
  })

  // Handle claim
  const handleClaimConversation = (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevenir navegación
    claimMutation.mutate(conversationId)
  }

  // Mutation para transferir conversación a equipo
  const transferMutation = useMutation({
    mutationFn: async ({ conversationId, teamId, shouldEscalate }: { conversationId: string; teamId: string; shouldEscalate: boolean }) => {
      // Primero escalar a humano si es necesario
      if (shouldEscalate) {
        await conversationsApi.escalateConversation(conversationId, { mode: 'human' })
      }
      // Luego transferir a equipo
      return conversationsApi.transferConversation(conversationId, teamId)
    },
    onMutate: async ({ conversationId }) => {
      setTransferringId(conversationId)
      setShowTeamSelector(null)
    },
    onSuccess: () => {
      toast.success('Conversación movida al equipo', {
        description: 'La conversación ha sido transferida exitosamente',
      })

      // Invalidar queries para refrescar
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (error: any) => {
      toast.error('Error al mover conversación', {
        description: error.response?.data?.message || 'Intenta nuevamente',
      })
    },
    onSettled: () => {
      setTransferringId(null)
    },
  })

  const initiateConversationMutation = useMutation({
    mutationFn: (payload: InitiateConversationRequest) => conversationsApi.initiateConversation(payload),
    onSuccess: (result) => {
      toast.success('Chat nuevo creado', {
        description: 'Se inició la conversación y se envió el primer mensaje.',
      })
      setShowNewChatModal(false)
      setNewChatPhone('')
      setNewChatMessage('Hola, quería escribirte por WhatsApp.')
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      if (result?.conversationId) {
        navigate(`/app/conversations/${result.conversationId}`)
      }
    },
    onError: (error: any) => {
      toast.error('No se pudo iniciar el chat', {
        description: error.response?.data?.error || 'Intenta nuevamente.',
      })
    },
  })

  // Handle transfer
  const handleTransferConversation = (conversationId: string, teamId: string, currentMode: 'bot' | 'human') => {
    const shouldEscalate = currentMode === 'bot'
    transferMutation.mutate({ conversationId, teamId, shouldEscalate })
  }

  const handleCreateNewChat = () => {
    const phone = newChatPhone.trim()
    const message = newChatMessage.trim()

    if (!phone || !message) {
      toast.error('Completa número y mensaje', {
        description: 'Necesitamos ambos campos para iniciar el chat.',
      })
      return
    }

    initiateConversationMutation.mutate({
      waContact: phone,
      content: message,
      channelInstanceId: selectedChannelInstanceId || undefined,
    })
  }

  // Filtrar conversaciones según tab activo
  const myConversations = conversations.filter(conv =>
    conv.assignedUserId === user?.id
  )

  const teamQueueConversations = conversations.filter((conv) => {
    const isUnassigned = !conv.assignedUserId
    const isQueueStatus = conv.status === 'QUEUED'
    const isOpenWithoutAgent = conv.status === 'open'
    // Include queued chats even when assignedTeamId is missing to avoid invisible cases.
    return isUnassigned && (isQueueStatus || isOpenWithoutAgent)
  })

  const allConversations = conversations // Todas las conversaciones sin filtro

  const displayedConversations =
    activeTab === 'mine' ? myConversations :
    activeTab === 'team' ? teamQueueConversations :
    allConversations

  const unreadByChannelSummary = useMemo(() => {
    const raw = data?.unreadByChannel || {}
    return {
      whatsapp: (raw['WHATSAPP'] || 0) + (raw['WHATSAPP_CLOUD'] || 0),
      telegram: raw['TELEGRAM'] || 0,
      webchat: raw['WEBCHAT'] || 0,
      instagram: raw['INSTAGRAM'] || 0,
      messenger: raw['MESSENGER'] || 0,
    }
  }, [data?.unreadByChannel])

  const totalByChannelSummary = useMemo(() => {
    const raw = data?.totalByChannel
    if (raw) {
      return {
        all: raw['all'] ?? data?.pagination?.total ?? conversations.length,
        whatsapp: (raw['WHATSAPP'] || 0) + (raw['WHATSAPP_CLOUD'] || 0),
        telegram: raw['TELEGRAM'] || 0,
        webchat: raw['WEBCHAT'] || 0,
        instagram: raw['INSTAGRAM'] || 0,
        messenger: raw['MESSENGER'] || 0,
      }
    }
    const counts = {
      all: data?.pagination?.total ?? conversations.length,
      whatsapp: 0,
      telegram: 0,
      webchat: 0,
      instagram: 0,
      messenger: 0,
    }
    for (const c of conversations) {
      const type = (c.channelType || 'WHATSAPP').toUpperCase()
      if (type === 'WHATSAPP' || type === 'WHATSAPP_CLOUD') counts.whatsapp++
      else if (type === 'TELEGRAM') counts.telegram++
      else if (type === 'WEBCHAT') counts.webchat++
      else if (type === 'INSTAGRAM') counts.instagram++
      else if (type === 'MESSENGER') counts.messenger++
    }
    return counts
  }, [data?.totalByChannel, data?.pagination?.total, conversations])

  const getChannelCount = (channelType?: string): number => {
    if (!channelType || channelType === 'all') {
      return totalByChannelSummary.all
    }
    const channelUpper = channelType.toUpperCase()
    if (filters.channelType && filters.channelType.toUpperCase() === channelUpper && data?.pagination?.total !== undefined) {
      return data.pagination.total
    }
    if (channelUpper === 'WHATSAPP' || channelUpper === 'WHATSAPP_CLOUD') {
      return totalByChannelSummary.whatsapp
    }
    if (channelUpper === 'TELEGRAM') {
      return totalByChannelSummary.telegram
    }
    if (channelUpper === 'WEBCHAT') {
      return totalByChannelSummary.webchat
    }
    if (channelUpper === 'INSTAGRAM') {
      return totalByChannelSummary.instagram
    }
    if (channelUpper === 'MESSENGER') {
      return totalByChannelSummary.messenger
    }
    return 0
  }

  const getChannelFriendlyLabel = (type?: string) => {
    switch (type) {
      case 'WHATSAPP':
      case 'WHATSAPP_CLOUD':
        return 'WhatsApp'
      case 'TELEGRAM':
        return 'Telegram'
      case 'WEBCHAT':
        return 'WebChat'
      case 'INSTAGRAM':
        return 'Instagram'
      case 'MESSENGER':
        return 'Facebook'
      default:
        return type || 'Todos'
    }
  }

  const hasActiveFilters = useMemo(() => {
    const isCustomChannel = filters.channelType && filters.channelType !== 'all'
    const isCustomStatus = filters.status && filters.status !== 'open'
    const isCustomMode = filters.mode && filters.mode !== 'all'
    const hasTags = Boolean(filters.tags && filters.tags.length > 0)
    const hasCloseReason = Boolean(filters.closeReason && filters.closeReason !== 'all')
    return Boolean(isCustomChannel || isCustomStatus || isCustomMode || hasTags || hasCloseReason || hasUnreadFilter)
  }, [filters.channelType, filters.status, filters.mode, filters.tags, filters.closeReason, hasUnreadFilter])

  // Abrir conversación
  // Formato de contador de pestaña: " (N)" o vacío si no hay dato / es 0
  const formatTabCount = (n?: number) => (n && n > 0 ? ` (${n > 99 ? '99+' : n})` : '')

  // Marcar como leída desde el menú de la fila (optimista + rollback)
  const handleMarkAsRead = async (conversationId: string) => {
    const previous = useConversationsStore.getState().conversations.find((c) => c.id === conversationId)?.unreadCount ?? 0
    updateConversation(conversationId, { unreadCount: 0 })
    try {
      await conversationsApi.markConversationAsRead(conversationId)
      queryClient.invalidateQueries({ queryKey: ['conversations', 'counts'] })
    } catch {
      updateConversation(conversationId, { unreadCount: previous })
      toast.error('No se pudo marcar como leída', { description: 'Intenta nuevamente' })
    }
  }

  const handleOpenConversation = (conversationId: string, isTeamQueue: boolean) => {
    // Update optimista: marcar como leída localmente y en cache de React Query al abrir
    updateConversation(conversationId, { unreadCount: 0 })
    queryClient.setQueriesData({ queryKey: ['conversations'] }, (old: any) => {
      if (!old?.conversations) return old
      return {
        ...old,
        conversations: old.conversations.map((c: any) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c
        ),
      }
    })
    if (isTeamQueue) {
      // Si es de la cola del equipo, mostrar en modo lectura
      navigate(`/app/conversations/${conversationId}?readonly=true`)
    } else {
      navigate(`/app/conversations/${conversationId}`)
    }
  }

  // Obtener avatar del contacto
  const getContactAvatar = (waContact: string, contactName?: string) => {
    if (contactName) {
      return contactName.slice(0, 2).toUpperCase()
    }
    const digits = waContact.replace(/\D/g, '').slice(-4)
    return digits.slice(0, 2)
  }

  // Formatear tiempo relativo
  const formatTimeAgo = (date: Date) => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Ahora'
    if (diffInMinutes < 60) return `${diffInMinutes}m`

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays === 1) return 'Ayer'
    if (diffInDays < 7) return `${diffInDays}d`

    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
  }

  // Obtener color del avatar
  const getAvatarColor = (waContact: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ]
    const index = waContact.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[index % colors.length]
  }

  // Get channel icon
  const getChannelIcon = (channelType?: string) => {
    switch(channelType) {
      case 'WHATSAPP':
      case 'WHATSAPP_CLOUD':
        return <MessageCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
      case 'TELEGRAM':
        return <Send className="h-4 w-4 text-blue-500 flex-shrink-0" />
      case 'WEBCHAT':
        return <Globe className="h-4 w-4 text-gray-500 flex-shrink-0" />
      case 'MESSENGER':
        return <MessageSquare className="h-4 w-4 text-blue-600 flex-shrink-0" />
      case 'INSTAGRAM':
        return <MessageSquare className="h-4 w-4 text-pink-500 flex-shrink-0" />
      default:
        return <MessageCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> // Default WA
    }
  }

  const getTeamNameById = (teamId?: string | null) => {
    if (!teamId || !teamsData) return null
    const team = teamsData.find((t) => t.id === teamId)
    return team ? team.name : null
  }

  const totalUnread = conversations.reduce(
    (acc, c) => acc + (openConversationId && c.id === openConversationId ? 0 : (c.unreadCount || 0)),
    0
  )

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center bg-white px-4 py-3 justify-between flex-shrink-0">
        <button
          type="button"
          onClick={() => refetch()}
          aria-label="Actualizar conversaciones"
          title="Actualizar"
          className="flex items-center justify-center h-10 w-10 text-[#075f55] hover:bg-slate-100 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#075f55]"
        >
          <RefreshCw className={`h-5 w-5 ${queryLoading ? 'animate-spin' : ''}`} />
        </button>
        <h1 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
          Mensajes{' '}
          {totalUnread > 0 && (
            <span className="bg-[#075f55]/10 text-[#075f55] px-2 py-0.5 rounded-full text-xs ml-1">
              {totalUnread}
            </span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewChatModal(true)}
            aria-label="Nueva conversación"
            className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#064e46]"
            title="Nueva conversación"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Nueva conversación</span>
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Filtros"
            aria-expanded={showFilters}
            title="Filtros"
            className={`flex items-center justify-center h-10 w-10 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#075f55] ${
              showFilters ? 'bg-[#075f55] text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Filter className="h-5 w-5" />
          </button>

        </div>
      </div>

      {/* Status Filter Tabs (Abiertas / Cerradas) */}
      <div className="bg-white px-4 pt-0.5 pb-2 flex-shrink-0">
        <div className="flex border-b border-slate-100 gap-2">
          <button
            type="button"
            onClick={() => handleStatusChange('open')}
            className={`pb-2 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              (filters.status || 'open') === 'open'
                ? 'border-[#075f55] text-[#075f55]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span>Abiertas</span>
            {viewCounts?.open !== undefined && (
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full font-bold transition-colors ${
                  (filters.status || 'open') === 'open'
                    ? 'bg-[#075f55]/10 text-[#075f55]'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {viewCounts.open}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleStatusChange('closed')}
            className={`pb-2 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              filters.status === 'closed'
                ? 'border-[#075f55] text-[#075f55]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span>Cerradas</span>
            {viewCounts?.closed !== undefined && (
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full font-bold transition-colors ${
                  filters.status === 'closed'
                    ? 'bg-[#075f55]/10 text-[#075f55]'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {viewCounts.closed}
              </span>
            )}
          </button>
          {filters.status === 'all' && (
            <button
              type="button"
              className="pb-2 px-3 text-xs font-bold border-b-2 border-[#075f55] text-[#075f55] transition-all flex items-center gap-1.5"
            >
              <span>Todas</span>
              {(viewCounts?.open !== undefined || viewCounts?.closed !== undefined) && (
                <span className="text-[11px] px-2 py-0.5 rounded-full font-bold bg-[#075f55]/10 text-[#075f55]">
                  {(viewCounts?.open ?? 0) + (viewCounts?.closed ?? 0)}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-4 pb-3 bg-white flex-shrink-0">
        <div className="relative flex items-center">
          <Search className="absolute left-3 text-slate-400 h-5 w-5 pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar en conversaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-100 rounded-xl pl-10 pr-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#075f55]/20 text-slate-900 placeholder:text-slate-400 text-sm border-none"
          />
          {isSearching ? (
            <Loader2 className="absolute right-3 h-4 w-4 text-[#075f55] animate-spin" />
          ) : searchTerm ? (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white px-4 pb-2 flex-shrink-0">
        <div className="flex p-1 bg-slate-100 rounded-xl">
          <button
            onClick={() => setActiveTab('mine')}
            className={`flex-1 py-2 px-1 text-center rounded-lg transition-all text-xs font-bold ${
              activeTab === 'mine'
                ? 'bg-white text-[#075f55] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Asignados{formatTabCount(viewCounts?.mine ?? (myConversations.length || undefined))}
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`flex-1 py-2 px-1 text-center rounded-lg transition-all text-xs font-bold ${
              activeTab === 'team'
                ? 'bg-white text-amber-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Equipo{formatTabCount(viewCounts?.team ?? (teamQueueConversations.length || undefined))}
          </button>
          {canViewAllConversations && (
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2 px-1 text-center rounded-lg transition-all text-xs font-bold ${
                activeTab === 'all'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Todos{formatTabCount(viewCounts?.all)}
            </button>
          )}
        </div>
      </div>

      {/* Channel Chips */}
      <ChannelFilterBar
        selectedChannelType={filters.channelType}
        onSelectChannel={(channelType) => setFilters({ ...filters, channelType, page: 1 })}
        unreadCounts={unreadByChannelSummary}
        totalCounts={totalByChannelSummary}
      />

      {/* Active Filters Bar */}
      {hasActiveFilters && (
        <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 border-y border-slate-100 overflow-x-auto text-xs flex-shrink-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <span className="text-slate-400 font-semibold flex-shrink-0">Filtros:</span>
          {hasUnreadFilter && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              No leídas
              <button
                type="button"
                onClick={() => {
                  setLocalFilters((prev) => ({ ...prev, hasUnread: undefined }))
                  setFilters({ ...filters, hasUnread: undefined, page: 1 })
                  if (searchParams.has('hasUnread')) setSearchParams({}, { replace: true })
                }}
                className="hover:text-red-500 ml-0.5"
                title="Quitar filtro de no leídas"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.channelType && filters.channelType !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              Canal: {getChannelFriendlyLabel(filters.channelType)} ({getChannelCount(filters.channelType)})
              <button
                type="button"
                onClick={() => setFilters({ ...filters, channelType: 'all', page: 1 })}
                className="hover:text-red-500 ml-0.5 text-slate-400 hover:text-slate-600"
                title="Quitar filtro de canal"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.status && filters.status !== 'open' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              Estado: {filters.status === 'closed' ? 'Cerradas' : 'Todas'}
              <button
                type="button"
                onClick={() => {
                  const updated = { ...filters, status: 'open' as const, page: 1 }
                  setFilters(updated)
                  setLocalFilters((prev) => ({ ...prev, status: 'open' }))
                }}
                className="hover:text-red-500 ml-0.5"
                title="Quitar filtro de estado"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.mode && filters.mode !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              Modo: {filters.mode === 'bot' ? 'Bot' : 'Manual'}
              <button
                type="button"
                onClick={() => {
                  const updated = { ...filters, mode: 'all' as const, page: 1 }
                  setFilters(updated)
                  setLocalFilters((prev) => ({ ...prev, mode: 'all' }))
                }}
                className="hover:text-red-500 ml-0.5"
                title="Quitar filtro de modo"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.tags && filters.tags.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              <TagIcon className="h-3 w-3 text-slate-400" />
              {filters.tags.length} {filters.tags.length === 1 ? 'etiqueta' : 'etiquetas'}
              <button
                type="button"
                onClick={() => {
                  const updated = { ...filters, tags: undefined, page: 1 }
                  setFilters(updated)
                  setSelectedTags([])
                }}
                className="hover:text-red-500 ml-0.5"
                title="Quitar filtro de etiquetas"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.closeReason && filters.closeReason !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-medium shadow-2xs">
              Motivo: {getReasonFilterLabel(filters.closeReason)}
              <button
                type="button"
                onClick={() => {
                  const updated = { ...filters, closeReason: undefined, page: 1 }
                  setFilters(updated)
                  setLocalFilters((prev) => ({ ...prev, closeReason: undefined }))
                }}
                className="hover:text-red-500 ml-0.5"
                title="Quitar filtro de motivo"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-[#075f55] hover:text-[#064e46] font-bold text-xs ml-auto flex-shrink-0 pl-2"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="mx-4 mb-3 p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3 flex-shrink-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Estado</label>
              <select
                value={localFilters.status || 'open'}
                onChange={(e) => {
                  const newStatus = e.target.value as any
                  setLocalFilters({
                    ...localFilters,
                    status: newStatus,
                    closeReason: newStatus === 'closed' ? localFilters.closeReason : undefined,
                  })
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#075f55]/20 bg-white"
              >
                <option value="open">Abiertas</option>
                <option value="closed">Cerradas</option>
                <option value="all">Todas</option>
              </select>
            </div>
            {localFilters.status === 'closed' ? (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Motivo de Cierre
                </label>
                <select
                  value={localFilters.closeReason || 'all'}
                  onChange={(e) => setLocalFilters({ ...localFilters, closeReason: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#075f55]/20 bg-white"
                >
                  <option value="all">Todos los motivos</option>
                  <optgroup label="Agrupados">
                    <option value="won">🎉 Solo Ventas Concretadas (Won)</option>
                    <option value="lost">❌ Solo No Concretadas (Lost)</option>
                  </optgroup>
                  <optgroup label="Ventas Ganadas (Won)">
                    <option value="SALE_CONFIRMED">Pedido Confirmado</option>
                    <option value="PAYMENT_RECEIVED">Pago Verificado</option>
                    <option value="ORDER_DELIVERED">Pedido Entregado</option>
                  </optgroup>
                  <optgroup label="Ventas No Concretadas (Lost)">
                    <option value="ABANDONED_CART">Carrito Abandonado</option>
                    <option value="OUT_OF_COVERAGE">Fuera de Cobertura</option>
                    <option value="OUT_OF_STOCK">Sin Stock / Agotado</option>
                    <option value="PRICE_OBJECTION">Objeción de Precio</option>
                    <option value="CANCELLED_BY_CLIENT">Cancelado por el Cliente</option>
                    <option value="CUSTOMER_SUPPORT">Consulta de Soporte</option>
                    <option value="SPAM_OR_WRONG_NUMBER">Spam o Número Erróneo</option>
                  </optgroup>
                </select>
              </div>
            ) : null}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Modo</label>
              <select
                value={localFilters.mode || 'all'}
                onChange={(e) => setLocalFilters({ ...localFilters, mode: e.target.value as any })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#075f55]/20 bg-white"
              >
                <option value="all">Todos</option>
                <option value="bot">Bot</option>
                <option value="human">Manual</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Etiquetas</label>
            <MultiSelect
              options={tagOptions}
              selected={selectedTags}
              onChange={setSelectedTags}
              placeholder="Seleccionar etiquetas"
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-[#075f55] text-white text-sm font-bold py-2 rounded-lg hover:bg-[#064e46] transition-colors"
            >
              Aplicar
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-slate-200 text-slate-700 text-sm font-bold py-2 px-4 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>
      )}

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <ConversationListSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 px-4">
            <p className="text-red-500 text-center text-sm">{error}</p>
            <button
              onClick={() => refetch()}
              className="bg-[#075f55] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#064e46] transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : displayedConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center select-none">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              activeTab === 'mine'
                ? 'bg-[#075f55]/10 text-[#075f55]'
                : activeTab === 'team'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-500'
            }`}>
              {activeTab === 'mine' ? (
                <Inbox className="h-8 w-8" />
              ) : activeTab === 'team' ? (
                <CheckCircle2 className="h-8 w-8" />
              ) : (
                <MessageSquare className="h-8 w-8" />
              )}
            </div>
            <h3 className="text-slate-900 font-bold text-base mb-1">
              {filters.status === 'closed'
                ? (activeTab === 'mine' ? 'No tienes chats cerrados' : 'No hay conversaciones cerradas')
                : activeTab === 'mine'
                  ? 'No tienes chats asignados'
                  : activeTab === 'team'
                    ? '¡Cola del equipo al día!'
                    : 'No hay conversaciones'}
            </h3>
            <p className="text-slate-500 text-xs max-w-xs mb-5">
              {filters.status === 'closed'
                ? 'Las conversaciones cerradas aparecerán aquí como historial.'
                : activeTab === 'mine'
                  ? 'Los chats asignados aparecerán aquí para que respondas de inmediato.'
                  : activeTab === 'team'
                    ? 'No hay chats esperando asignación en este momento.'
                    : 'No se encontraron conversaciones con los filtros actuales.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-2">
              <button
                type="button"
                onClick={() => setShowNewChatModal(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-4 py-2 text-xs font-bold text-white shadow-sm transition-colors hover:bg-[#064e46]"
              >
                <Send className="h-3.5 w-3.5" />
                Nueva conversación
              </button>
              {activeTab === 'mine' && teamQueueConversations.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveTab('team')}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-xs font-bold text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  <Users className="h-3.5 w-3.5" />
                  Ver cola del equipo ({teamQueueConversations.length})
                </button>
              )}
            </div>
          </div>
        ) : (
          displayedConversations.map((conversation) => {
            const avatarColor = getAvatarColor(conversation.waContact)
            const isUnread = (!openConversationId || conversation.id !== openConversationId) && conversation.unreadCount > 0
            const isTeamQueue = activeTab === 'team'
            const isClaiming = claimingId === conversation.id
            const teamName = conversation.assignedTeamId
              ? getTeamNameById(conversation.assignedTeamId)
              : null
            const isHitl = Boolean((conversation as any).isHitlPending)
            const isEscalated = Boolean(conversation.isEscalatedByBot || (conversation.mode === 'human' && (conversation.commercialStatus || conversation.escalationReason)))

            return (
              <motion.div
                key={conversation.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 4 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  ...(isHitl
                    ? {
                        backgroundColor: [
                          'rgba(254, 243, 199, 0.25)',
                          'rgba(254, 243, 199, 0.55)',
                          'rgba(254, 243, 199, 0.25)',
                        ],
                      }
                    : isEscalated
                      ? {
                          backgroundColor: [
                            'rgba(243, 232, 255, 0.25)',
                            'rgba(243, 232, 255, 0.55)',
                            'rgba(243, 232, 255, 0.25)',
                          ],
                        }
                      : {}),
                }}
                transition={{
                  backgroundColor: { duration: 2.8, repeat: Infinity, ease: 'easeInOut' },
                  default: { duration: 0.2 },
                }}
                aria-label={`Abrir conversación con ${conversation.contactName || conversation.waContact}${
                  isUnread ? `, ${conversation.unreadCount} sin leer` : ''
                }${conversation.hasPendingStaffConsultation ? ', tiene una duda por resolver' : ''}`}
                className={`flex items-center gap-3 p-4 cursor-pointer border-b border-slate-50 transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#075f55] ${
                  isTeamQueue
                    ? 'bg-[#075f55]/5 border-b-[#075f55]/10'
                    : isHitl
                      ? 'bg-amber-50/25 border-l-2 border-l-amber-500 hover:bg-amber-50/50'
                      : isEscalated
                        ? 'bg-purple-50/25 border-l-2 border-l-purple-500 hover:bg-purple-50/50'
                        : 'hover:bg-slate-50'
                }`}
                onClick={() => handleOpenConversation(conversation.id, activeTab === 'team')}
                onKeyDown={(e) => {
                  // Solo si el foco está en la fila misma (no en botones internos)
                  if (e.target !== e.currentTarget) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenConversation(conversation.id, activeTab === 'team')
                  }
                }}
              >
                {/* Avatar with channel icon */}
                <div className="relative flex-shrink-0">
                  <div className={`w-14 h-14 rounded-full ${avatarColor} flex items-center justify-center text-white font-semibold text-sm ${
                    isTeamQueue ? 'ring-2 ring-[#075f55] ring-offset-2' : ''
                  }`}>
                    {getContactAvatar(conversation.waContact, conversation.contactName)}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-slate-100">
                    {getChannelIcon(conversation.channelType)}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline mb-0.5">
                    <h3 className={`text-[15px] font-bold text-slate-900 truncate group-hover:text-[#075f55] transition-colors ${
                      isUnread ? 'text-slate-900' : ''
                    }`}>
                      {conversation.contactName || conversation.waContact}
                    </h3>
                    <span className={`text-[11px] font-semibold flex-shrink-0 ml-2 ${
                      isUnread ? 'text-[#075f55]' : 'text-slate-400'
                    }`}>
                      {conversation.lastMessageAt ? formatTimeAgo(conversation.lastMessageAt) : ''}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-sm text-slate-500 truncate flex-1">
                      {conversation.lastMessage || 'Sin mensajes'}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Commercial status badge */}
                      {conversation.commercialStatus && (
                        <span
                          title={
                            conversation.commercialStatus.productName
                              ? `Pedido: ${conversation.commercialStatus.productName}`
                              : 'Pedido por concretar'
                          }
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-emerald-300 bg-emerald-50 text-emerald-800 flex-shrink-0"
                        >
                          <ShoppingBag className="h-2.5 w-2.5 text-emerald-600" />
                          <span>
                            {conversation.commercialStatus.totalAmount
                              ? `$${conversation.commercialStatus.totalAmount}`
                              : conversation.commercialStatus.status === 'pickup'
                                ? 'Retiro'
                                : 'Pedido'}
                          </span>
                        </span>
                      )}

                      {conversation.hasPendingStaffConsultation && (
                        <PendingConsultationBadge question={conversation.pendingStaffConsultationQuestion} />
                      )}

                      {/* Mode badge */}
                      {isHitl ? (
                        <motion.span
                          title="Pausa HITL activa: Esperando respuesta o confirmación humana"
                          animate={{ scale: [1, 1.04, 1] }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-amber-500/50 text-amber-800 bg-amber-100/90 shadow-2xs flex-shrink-0"
                        >
                          <motion.span
                            className="w-1.5 h-1.5 rounded-full bg-amber-600"
                            animate={{ opacity: [1, 0.25, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                          />
                          <AlertCircle className="h-2.5 w-2.5 text-amber-700" />
                          <span>PAUSA HITL</span>
                        </motion.span>
                      ) : isEscalated ? (
                        <motion.span
                          title={conversation.escalationReason ? `Caso escalado por Agente IA: ${conversation.escalationReason}` : 'Caso escalado por el Agente IA para atención humana'}
                          animate={{ scale: [1, 1.03, 1] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-purple-500/50 text-purple-700 bg-purple-50 flex-shrink-0"
                        >
                          <motion.span
                            className="w-1.5 h-1.5 rounded-full bg-purple-600"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                          />
                          <Bot className="h-2.5 w-2.5 text-purple-600" />
                          <span>ESCALADO IA</span>
                        </motion.span>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border flex-shrink-0 ${
                          conversation.mode === 'bot'
                            ? 'border-blue-500/30 text-blue-600 bg-blue-50'
                            : 'border-orange-500/30 text-orange-600 bg-orange-50'
                        }`}>
                          {conversation.mode === 'bot' ? 'BOT' : 'MANUAL'}
                        </span>
                      )}

                      {/* Closure status badge */}
                      {conversation.status === 'closed' && (
                        <span
                          title={conversation.closeNotes ? `Cerrada: ${conversation.closeNotes}` : 'Conversación cerrada'}
                          className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border flex-shrink-0 ${
                            isClosureWon(conversation.closeReason)
                              ? 'border-emerald-500/30 text-emerald-700 bg-emerald-50'
                              : 'border-rose-500/30 text-rose-700 bg-rose-50'
                          }`}
                        >
                          {getClosureShortLabel(conversation.closeReason)}
                        </span>
                      )}

                      {/* Claim button (team queue) o badge de no leídos */}
                      {isTeamQueue ? (
                        <button
                          onClick={(e) => handleClaimConversation(conversation.id, e)}
                          disabled={isClaiming}
                          className="bg-[#075f55] hover:bg-[#064e46] text-white text-[11px] font-black uppercase px-4 py-1.5 rounded-full shadow-md active:scale-95 transition-all disabled:opacity-50"
                        >
                          {isClaiming ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : 'Tomar'}
                        </button>
                      ) : isUnread ? (
                        <span className="flex items-center justify-center bg-[#075f55] text-white text-[10px] font-bold h-6 min-w-[24px] px-1.5 rounded-full ring-4 ring-white shadow-sm">
                          {conversation.unreadCount}
                        </span>
                      ) : null}

                      {/* Menú de acciones ⋮ */}
                      <RowActionsMenu
                        ariaLabel={`Acciones para ${conversation.contactName || conversation.waContact}`}
                        actions={[
                          {
                            key: 'open',
                            label: 'Abrir chat',
                            icon: <ExternalLink className="h-4 w-4 text-slate-400" />,
                            onSelect: () => handleOpenConversation(conversation.id, isTeamQueue),
                          },
                          ...(isUnread
                            ? [{
                                key: 'read',
                                label: 'Marcar como leída',
                                icon: <CheckCheck className="h-4 w-4 text-slate-400" />,
                                onSelect: () => handleMarkAsRead(conversation.id),
                              }]
                            : []),
                          ...(isTeamQueue
                            ? [{
                                key: 'claim',
                                label: 'Tomar chat',
                                icon: <Users className="h-4 w-4 text-slate-400" />,
                                disabled: isClaiming,
                                onSelect: () => claimMutation.mutate(conversation.id),
                              }]
                            : []),
                          ...(canViewAllConversations && teamsData
                            ? [{
                                key: 'move',
                                label: 'Mover a equipo…',
                                icon: <Users className="h-4 w-4 text-slate-400" />,
                                disabled: transferringId === conversation.id,
                                onSelect: () => setShowTeamSelector(conversation.id),
                              }]
                            : []),
                        ]}
                      />
                      {showTeamSelector === conversation.id && teamsData && (
                        <div className="relative">
                          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowTeamSelector(null) }} />
                          <div className="absolute right-0 top-full mt-2 z-20 bg-white rounded-xl shadow-lg border border-slate-100 py-2 w-52 max-h-60 overflow-y-auto">
                            {teamsData.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-slate-500 text-center">Sin equipos</div>
                            ) : (
                              teamsData.map((team) => (
                                <button
                                  key={team.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleTransferConversation(conversation.id, team.id, conversation.mode)
                                  }}
                                  disabled={transferringId === conversation.id}
                                  className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700 disabled:opacity-50"
                                >
                                  <Users className="h-4 w-4 text-slate-400" />
                                  <span className="flex-1">{team.name}</span>
                                  {team.isDefault && <span className="text-xs text-slate-400">Default</span>}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Team name for queued */}
                  {isTeamQueue && teamName && (
                    <p className="text-[11px] text-amber-700 mt-0.5 truncate">En cola: {teamName}</p>
                  )}
                </div>
              </motion.div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="text-sm font-bold text-[#075f55] disabled:text-slate-300 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="text-sm text-slate-500">
            {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="text-sm font-bold text-[#075f55] disabled:text-slate-300 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}

      <Modal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        title="Nuevo chat"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Inicia una conversación saliente después de validar que el número existe en WhatsApp.
          </p>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Canal</label>
            <select
              value={selectedChannelInstanceId || ''}
              onChange={(event) => setSelectedChannelInstanceId(event.target.value || null)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800"
            >
              {(channelInstances as ChannelInstanceResponse[]).map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.instanceName} · {instance.channelType}
                </option>
              ))}
            </select>
            {channelInstances.length === 0 && (
              <p className="text-xs text-amber-700">
                No hay canales conectados. Conecta WhatsApp antes de iniciar un chat.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Número</label>
            <input
              value={newChatPhone}
              onChange={(event) => setNewChatPhone(event.target.value)}
              placeholder="+58 412 1234567"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-800"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mensaje inicial</label>
            <textarea
              value={newChatMessage}
              onChange={(event) => setNewChatMessage(event.target.value)}
              rows={4}
              className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-800"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowNewChatModal(false)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCreateNewChat}
              disabled={initiateConversationMutation.isPending || channelInstances.length === 0}
              className="rounded-md bg-[#075f55] px-3 py-2 text-sm font-medium text-white hover:bg-[#064e46] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {initiateConversationMutation.isPending ? 'Iniciando...' : 'Iniciar chat'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Mobile Floating Action Button (FAB) */}
      <button
        type="button"
        onClick={() => setShowNewChatModal(true)}
        aria-label="Nueva conversación"
        className="lg:hidden fixed bottom-20 right-4 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-[#075f55] text-white shadow-lg shadow-[#075f55]/30 hover:bg-[#064e46] active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#075f55]"
      >
        <Send className="h-5 w-5" />
      </button>
    </div>
  )
}
